import { useEffect, useRef, useState } from 'react';

// Miniature d'aperçu au survol de la barre de progression (web uniquement)
// Mode 1 (instantané) : planche de vignettes générée côté serveur (sprite + spriteMeta)
// Mode 2 (repli) : <video> caché positionné sur le temps survolé
export const ProgressPreview = ({ src, time, sprite, spriteMeta }) => {
  if (sprite && spriteMeta?.interval) {
    return <SpritePreview sprite={sprite} meta={spriteMeta} time={time} />;
  }
  return <VideoPreview src={src} time={time} />;
};

const DISPLAY_W = 176; // w-44

function SpritePreview({ sprite, meta, time }) {
  const [dims, setDims] = useState(null);
  useEffect(() => {
    const img = new Image();
    img.onload = () => setDims({ w: img.naturalWidth / (meta.cols || 10), h: img.naturalHeight / (meta.rows || 10) });
    img.src = sprite;
  }, [sprite, meta.cols, meta.rows]);

  if (!dims) return null;
  const cols = meta.cols || 10;
  const idx = Math.max(0, Math.min((meta.count || 1) - 1, Math.floor(time / meta.interval)));
  const col = idx % cols;
  const row = Math.floor(idx / cols);
  const scale = DISPLAY_W / dims.w;
  const dh = Math.round(dims.h * scale);
  return (
    <div className="rounded-md overflow-hidden bg-black border border-white/25 shadow-2xl mb-1.5"
      data-testid="progress-preview-thumb"
      style={{
        width: DISPLAY_W, height: dh,
        backgroundImage: `url(${sprite})`,
        backgroundSize: `${Math.round(dims.w * cols * scale)}px auto`,
        backgroundPosition: `-${Math.round(col * DISPLAY_W)}px -${Math.round(row * dh)}px`,
      }} />
  );
}

function VideoPreview({ src, time }) {
  const videoRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);
  const pendingRef = useRef(time);
  const seekingRef = useRef(false);

  useEffect(() => {
    pendingRef.current = time;
    const v = videoRef.current;
    if (!v || seekingRef.current || v.readyState === 0) return;
    seekingRef.current = true;
    try { v.currentTime = time; } catch { seekingRef.current = false; }
  }, [time]);

  const onSeeked = () => {
    setReady(true);
    seekingRef.current = false;
    const v = videoRef.current;
    if (v && Math.abs(v.currentTime - pendingRef.current) > 1.5) {
      seekingRef.current = true;
      try { v.currentTime = pendingRef.current; } catch { seekingRef.current = false; }
    }
  };

  if (!src || failed) return null;
  return (
    <div className={`w-44 aspect-video rounded-md overflow-hidden bg-black border border-white/25 shadow-2xl mb-1.5 transition-opacity duration-150 ${ready ? 'opacity-100' : 'opacity-0'}`} data-testid="progress-preview-thumb">
      <video
        ref={videoRef}
        src={src}
        muted
        playsInline
        preload="metadata"
        tabIndex={-1}
        className="w-full h-full object-cover"
        onError={() => setFailed(true)}
        onLoadedMetadata={() => {
          const v = videoRef.current;
          if (v && !seekingRef.current) {
            seekingRef.current = true;
            try { v.currentTime = pendingRef.current; } catch { seekingRef.current = false; }
          }
        }}
        onSeeked={onSeeked}
      />
    </div>
  );
}
